import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as bodyScrollLock from 'body-scroll-lock'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { graphql, useStaticQuery } from 'gatsby'
import React, { useEffect, useRef } from 'react'
import { useClickAway } from 'react-use'
import { useDataLayer } from '../../hooks/useDataLayer'
import { useSiteId } from '../../hooks/useSiteId'
import { useTranslations } from '../../hooks/useTranslations'
import ToolContactImage from '../../images/tool-contact.png'
import ToolEmergencyImage from '../../images/tool-emergency.png'
import { useToolOverlay } from '../Context/ToolOverlayContext'
import ContactTool from '../molecules/ContactTool'
import FooterContactForm from '../molecules/FooterContactForm'

const ToolOverlay = ({ onClose }) => {
  const { contextSectionLimit } = useToolOverlay()
  const dataLayer = useDataLayer()
  const ref = useRef()
  const translations = useTranslations()
  const siteId = useSiteId()

  const {
    craftContent: { globalSets: sites, entries },
  } = useStaticQuery(graphql`
    query SidebarQuery {
      craftContent {
        entries(type: "footer", section: "footer", siteId: [1, 2, 3]) {
          id
          siteId
          ... on CraftContent_footer_footer_Entry {
            footerSettings {
              __typename
              ... on CraftContent_footerSettings_contactTool_BlockType {
                id
                pageLink {
                  ...linkFragment
                }
              }
            }
          }
        }
        globalSets(handle: "sidebar", siteId: [1, 2, 3]) {
          siteId
          ... on CraftContent_sidebar_GlobalSet {
            planningToolCards {
              ... on CraftContent_planningToolCards_BlockType {
                id
                icon {
                  ... on CraftContent_images_Asset {
                    id
                    url
                  }
                }
                tags {
                  label
                }
                cardUrl
                cardContent
                heading
              }
            }
          }
        }
      }
    }
  `)
  const entry = entries.find(e => e.siteId === siteId)
  const contactPageSettings = entry.footerSettings.find(
    ({ __typename }) =>
      __typename === 'CraftContent_footerSettings_contactTool_BlockType',
  )
  const planningToolCards = sites.find(
    ({ siteId: id }) => id === siteId,
  ).planningToolCards

  useEffect(() => {
    bodyScrollLock.disableBodyScroll(ref.current)

    return () => bodyScrollLock.clearAllBodyScrollLocks()
  }, [])

  useClickAway(ref, () => onClose())

  return (
    <motion.div
      className="overflow-hidden bg-black bg-opacity-50 backdrop-blur-sm fixed inset-0 z-[9999]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        ref={ref}
        className="w-full sm:w-[520px] fixed top-0 right-0 bottom-0 bg-white px-16 sm:px-14 pt-32 pb-16 sm:py-32 overflow-y-auto"
        initial={{ x: 520 }}
        animate={{ x: 0 }}
        exit={{ x: 520 }}
        transition={{ type: 'ease' }}
      >
        {(contextSectionLimit === null ||
          contextSectionLimit === 'emergency') && (
          <div
            className={clsx('pb-10 mb-10 sm:px-6 border-gray-4', {
              'border-b': contextSectionLimit !== 'emergency',
            })}
          >
            <div className="mb-3">
              <img src={ToolEmergencyImage} alt="" className="w-5 h-auto" />
            </div>
            <p className="font-bold u-p mb-2.5">
              {translations.forTrappedPeople}:
              <br />
              {translations.breakdownServiceAt}
            </p>
            <div className="mb-5 text-[23px] font-bold leading-6 text-red">
              <a
                href={`tel:${translations.servicePhoneNumber}`}
                onClick={() =>
                  dataLayer.push({
                    eventCategory: 'Telefonnummern',
                    eventAction: 'click',
                    eventLabel: translations.servicePhoneNumber,
                    event_location: null,
                  })
                }
              >
                {translations.servicePhoneNumber}
              </a>
            </div>
            <p
              className="u-p link-wrapper"
              dangerouslySetInnerHTML={{
                __html: translations.pleaseReportFaults,
              }}
            />
          </div>
        )}

        {(contextSectionLimit === null || contextSectionLimit === 'people') && (
          <div
            className={clsx('mb-10 sm:px-6', [
              contextSectionLimit === 'people' ? 'pb-6.5' : 'pb-10',
            ])}
          >
            <div className="mb-3">
              <img src={ToolContactImage} alt="" className="w-5 h-auto" />
            </div>
            <p className="mb-5 font-bold u-p">{translations.findAdvisor}</p>
            <QueryClientProvider client={new QueryClient()}>
              <ContactTool />
            </QueryClientProvider>
          </div>
        )}

        {(contextSectionLimit === null ||
          contextSectionLimit === 'planningtool') && (
          <div className="space-y-[60px]">
            {planningToolCards.map(card => (
              <a
                href={card.cardUrl}
                target="_blank"
                rel="noreferrer noopener"
                key={card.id}
                className="block focus:outline-none px-6 pt-16 pb-6 bg-white shadow-box rounded-[5px] relative"
                onClick={() =>
                  dataLayer.push({
                    event: 'userinteraction',
                    eventCategory: 'Links',
                    eventAction: 'click',
                    eventLabel: card.heading,
                    event_location: 'sidemenu',
                  })
                }
              >
                <img
                  src={card.icon[0]?.url}
                  alt=""
                  className="absolute w-20 h-20 rounded-full left-6 -top-10"
                />
                <h3 className="font-bold u-p mb-2.5">{card.heading}</h3>
                <p
                  className="mb-4 u-p"
                  dangerouslySetInnerHTML={{ __html: card.cardContent }}
                />
                <div className="flex flex-wrap pr-3 lg:pr-5">
                  {card.tags.map(tag => (
                    <span
                      key={tag.label}
                      className="bg-gray-2 mb-2 px-2 leading-[22px] whitespace-nowrap rounded-full mr-2 text-[16px]"
                    >
                      {tag.label}
                    </span>
                  ))}
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 16 16"
                  className="absolute bottom-3 right-3"
                >
                  <path
                    stroke="#000"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4.465 1.636h9.9m0 0v9.9m0-9.9L1.635 14.364"
                  ></path>
                </svg>
              </a>
            ))}
          </div>
        )}

        {contextSectionLimit === 'people' && (
          <div className="mt-6.5 ml-6">
            <FooterContactForm
              link={contactPageSettings.pageLink}
              onClick={onClose}
            />
          </div>
        )}
      </motion.div>

      <motion.button
        type="button"
        onClick={onClose}
        className="w-[30px] sm:w-[54px] h-[30px] sm:h-[54px] rounded-full bg-black text-white flex items-center justify-center absolute right-4 sm:right-[493px] top-4 sm:top-10 transition-color hover:bg-white hover:text-black hover:shadow-tpmp"
        initial={{ x: 520 }}
        animate={{ x: 0 }}
        exit={{ x: 520 }}
        transition={{ type: 'ease' }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          fill="none"
          viewBox="0 0 22 22"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 5l6 6-6 6M17 5l-6 6 6 6"
          ></path>
        </svg>
      </motion.button>
    </motion.div>
  )
}

export default ToolOverlay
